import React, { useState, useMemo, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, Tooltip, LabelList, CartesianGrid } from "recharts";
import { Info, TrendingUp, Users, User, Check, X, ShieldCheck, AlertTriangle, Gift, ExternalLink, HelpCircle } from "lucide-react";

import { FLAT_RATE_MONTH, JOINT_REDUCTION } from "./tax-constants.js";
import { scenarioSolo, scenarioCollab } from "./tax-engine.js";
import { I18N, LANG_LABELS, detectLang, localeFor } from "./translations.js";
import { FAQ } from "./faq.js";

const C = {
  ink: "#1c1a17", paper: "#f4efe6", card: "#fbf8f1", line: "#ddd3c2",
  terra: "#c4502a", terraDeep: "#9a3a1c", teal: "#1f6f63", gold: "#b8893a", muted: "#7c7468",
};

/* ---- breakdown row builder ---- */
function tramoLabel(idx, t) {
  return idx < 3 ? `${t("bd_tramo_reduced")} R${idx + 1}` : `${t("bd_tramo_general")} ${idx - 2}`;
}

function buildRows(view, r, kids, t, eur) {
  const rows = [];
  const H = (label) => rows.push({ kind: "head", label });
  const S = (label) => rows.push({ kind: "sub", label });
  const R = (label, value, note, unit) => rows.push({ kind: "row", label, value, note, unit });
  const TT = (label, value, note) => rows.push({ kind: "total", label, value, note });
  const G = (label, value) => rows.push({ kind: "grand", label, value });

  const ssBlock = (ss, whoLabel, whoLabelLc, generic) => {
    if (ss.flat) { R(`${whoLabel} · ${t("bd_flat_first")}`, ss.monthlyQuota, t("bd_flat_note"), "eurMonth"); TT(`${t("bd_quota_annual")} ${whoLabelLc}`, ss.annual); return; }
    R(`${whoLabel} · ${t("bd_rendimiento")}`, ss._base, t("bd_rendimiento_note"), "eur");
    R(`${t("bd_base_computable")} (−${Math.round(generic * 100)}% ${t("bd_base_computable_note")})`, ss.computable);
    R(t("bd_base_monthly"), ss.monthly, tramoLabel(ss.idx, t), "eurMonth");
    R(t("bd_base_min"), ss.base, ss.flooredByCollab ? t("bd_base_min_floor") : t("bd_base_min_from"), "eurMonth");
    R(t("bd_quota_monthly"), ss.monthlyQuota, null, "eurMonth");
    TT(`${t("bd_quota_annual")} ${whoLabelLc} (× 12)`, ss.annual);
  };

  if (view === "solo") {
    const b = r.solo.bd; b.ssTit._base = b.P;
    H(t("bd_ss_title"));
    ssBlock(b.ssTit, t("bd_titular"), t("bd_titular_lc"), b.ssTit.generic);
    H(`${t("bd_irpf_solo")} ${b.joint ? t("sc_solo_sub_joint") : t("sc_solo_sub_indiv")}`);
    R(t("bd_rend_previo"), b.P - b.ssTit.annual);
    R(t("bd_dj"), -b.dj);
    TT(t("bd_net_activity"), b.netActividad);
    if (b.joint) R(t("bd_red_joint"), -JOINT_REDUCTION);
    TT(t("bd_base_liq"), b.irpf.bl);
    R(t("bd_min_family"), b.irpf.sMin, `${t("bd_min_family_note")} + ${kids} ${t("bd_kids")}`);
    R(t("bd_cuota_state"), b.irpf.state, "escala 9,5–24,5 %");
    R(t("bd_cuota_cv"), b.irpf.cv, "escala 9–29,5 %");
    TT(t("bd_irpf_total"), b.irpf.total);
    G(t("bd_grand_tax"), r.solo.ss + r.solo.tax);
    G(t("bd_grand_net"), r.solo.take);
    return rows;
  }

  const b = (view === "cj" ? r.cj : r.ci).bd;
  b.ssTit._base = b.titularBiz; b.ssWife._base = b.W;
  H(t("bd_ss_title_two"));
  S(t("bd_titular_section"));
  ssBlock(b.ssTit, t("bd_titular"), t("bd_titular_lc"), b.ssTit.generic);
  S(t("bd_collab_section"));
  R(t("bd_wage_assigned"), b.W, t("bd_wage_deductible"));
  ssBlock(b.ssWife, t("bd_collab"), t("bd_collab_lc"), b.ssWife.generic);
  G(t("bd_ss_total"), r[view].ss);

  if (view === "cj") {
    H(t("bd_irpf_joint_long"));
    R(t("bd_titular_net"), b.titularNet, t("bd_titular_net_note"));
    R(t("bd_wife_net"), b.wifeNet, `${t("bd_wife_net_note")} ${eur(b.wifeRed)}`);
    R(t("bd_red_joint"), -JOINT_REDUCTION);
    TT(t("bd_base_liq_joint"), b.blMain);
    R(t("bd_min_family"), b.irMain.sMin);
    R(t("bd_cuota_state"), b.irMain.state);
    R(t("bd_cuota_cv"), b.irMain.cv);
    TT(t("bd_irpf_total"), b.irMain.total);
  } else {
    H(t("bd_irpf_indiv_long"));
    S(t("bd_titular_section"));
    R(t("bd_base_liq_simple"), b.titularNet);
    R(t("bd_cuota_state"), b.irMain.state);
    R(t("bd_cuota_cv"), b.irMain.cv);
    TT(t("bd_irpf_owner"), b.irMain.total);
    S(t("bd_collab_section"));
    R(t("bd_base_liq_simple"), b.wifeNet, `${t("bd_wife_reduction")} ${eur(b.wifeRed)}`);
    R(t("bd_cuota_state"), b.irWife.state);
    R(t("bd_cuota_cv"), b.irWife.cv);
    TT(t("bd_irpf_collab"), b.irWife.total);
    TT(t("bd_irpf_both"), b.irMain.total + b.irWife.total);
  }
  G(t("bd_grand_tax"), r[view].ss + r[view].tax);
  G(t("bd_grand_net"), r[view].take);
  return rows;
}

function CalcTable({ rows, eur }) {
  const fmtVal = (v, unit) => unit === "eurMonth" ? eur(v) + "/mes" : eur(v);
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, tableLayout: "fixed" }}>
      <colgroup><col style={{ width: "62%" }} /><col style={{ width: "38%" }} /></colgroup>
      <tbody>
        {rows.map((row, i) => {
          if (row.kind === "head")
            return <tr key={i}><td colSpan={2} style={{ paddingTop: i === 0 ? 4 : 18, paddingBottom: 6 }}>
              <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 15, color: C.terraDeep }}>{row.label}</span></td></tr>;
          if (row.kind === "sub")
            return <tr key={i}><td colSpan={2} style={{ paddingTop: 10, paddingBottom: 3, fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: C.muted }}>{row.label}</td></tr>;
          const isTotal = row.kind === "total", isGrand = row.kind === "grand";
          return (
            <tr key={i} style={{ borderTop: isGrand ? `2px solid ${C.ink}` : isTotal ? `1px solid ${C.line}` : "none" }}>
              <td style={{ padding: isGrand ? "8px 0 2px" : "3px 0", color: C.ink, wordBreak: "break-word" }}>
                <span style={{ fontWeight: isGrand ? 700 : isTotal ? 600 : 400 }}>{row.label}</span>
                {row.note && <span style={{ display: "block", fontSize: 10.5, color: C.muted, fontStyle: "italic" }}>{row.note}</span>}
              </td>
              <td className="num" style={{ textAlign: "right", whiteSpace: "nowrap", padding: isGrand ? "8px 0 2px" : "3px 0",
                fontWeight: isGrand ? 600 : isTotal ? 600 : 400, fontSize: isGrand ? 15 : 13,
                color: (typeof row.value === "number" && row.value < 0) ? C.terra : (isGrand ? C.teal : C.ink) }}>
                {fmtVal(row.value, row.unit)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Bullets({ items, color, Icon }) {
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map((txt, i) => (
        <li key={i} style={{ display: "flex", gap: 9, fontSize: 13, lineHeight: 1.45, color: C.ink }}>
          {Icon
            ? <Icon size={16} color={color} strokeWidth={2.4} style={{ flexShrink: 0, marginTop: 2 }} />
            : <span style={{ width: 5, height: 5, borderRadius: "50%", background: color, flexShrink: 0, marginTop: 7 }} />}
          <span style={{ minWidth: 0, wordBreak: "break-word" }}>{txt}</span>
        </li>
      ))}
    </ul>
  );
}
function OptionCol({ title, accent, pros, cons, prosLabel, consLabel }) {
  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: 10, padding: 16 }}>
      <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 16, marginBottom: 12, color: accent }}>{title}</div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: C.teal, marginBottom: 7 }}>{prosLabel}</div>
      <Bullets items={pros} color={C.teal} Icon={Check} />
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: C.terra, margin: "13px 0 7px" }}>{consLabel}</div>
      <Bullets items={cons} color={C.terra} Icon={X} />
    </div>
  );
}
function InfoSection({ Icon, color, title, items, bulletIcon, intro }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Icon size={18} color={color} strokeWidth={2.2} />
        <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 16 }}>{title}</span>
      </div>
      {intro && <p style={{ margin: "0 0 12px", fontSize: 12.5, color: C.ink, lineHeight: 1.5 }}>{intro}</p>}
      <Bullets items={items} color={color} Icon={bulletIcon} />
    </div>
  );
}

function Slider({ label, value, min, max, step, onChange, suffix = "€", hint }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6, gap: 8 }}>
        <label style={{ fontSize: 12.5, letterSpacing: ".04em", textTransform: "uppercase", color: C.muted, fontWeight: 600, minWidth: 0, flex: "1 1 auto" }}>{label}</label>
        <div style={{ display: "flex", alignItems: "baseline", gap: 4, flexShrink: 0 }}>
          <input type="number" value={value} min={min} max={max} step={step}
            onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || 0)))}
            style={{ width: 84, textAlign: "right", fontFamily: "'DM Mono', monospace", fontSize: 16, fontWeight: 500,
              background: "transparent", border: "none", borderBottom: `1.5px solid ${C.line}`, color: C.ink, padding: "2px 2px", minWidth: 0 }} />
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: C.muted }}>{suffix}</span>
        </div>
      </div>
      <input type="range" value={value} min={min} max={max} step={step}
        onChange={(e) => onChange(Number(e.target.value))} className="slider" />
      {hint && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

export default function App() {
  const [lang, setLang] = useState("es");
  useEffect(() => { setLang(detectLang()); }, []);
  const t = (k) => (I18N[k] && I18N[k][lang]) || (I18N[k] && I18N[k].es) || k;
  const eur = (n) => new Intl.NumberFormat(localeFor(lang), { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.round(n));

  const [revenue, setRevenue] = useState(50000);
  const [expenses, setExpenses] = useState(10000);
  const [split, setSplit] = useState(40);
  const [kids, setKids] = useState(1);
  const [hasSpouse, setHasSpouse] = useState(true);
  const [years, setYears] = useState(3);
  const [showCalc, setShowCalc] = useState(false);
  const [calcView, setCalcView] = useState("ci");
  const [showInfo, setShowInfo] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [openFaqIdx, setOpenFaqIdx] = useState(null);

  const flat = years === 1;
  const P = Math.max(0, revenue - expenses);

  const r = useMemo(() => {
    const solo = scenarioSolo(revenue, expenses, kids, hasSpouse, flat);
    const cj = scenarioCollab(revenue, expenses, split / 100, true, kids, flat);
    const ci = scenarioCollab(revenue, expenses, split / 100, false, kids, flat);
    return { solo, cj, ci };
  }, [revenue, expenses, split, kids, hasSpouse, flat]);

  const cols = [
    { key: "solo", name: t("sc_solo_name"), sub: hasSpouse ? t("sc_solo_sub_joint") : t("sc_solo_sub_indiv"), icon: User, d: r.solo, accent: C.ink },
    { key: "cj", name: t("sc_collab_name"), sub: t("sc_collab_joint"), icon: Users, d: r.cj, accent: C.teal },
    { key: "ci", name: t("sc_collab_name"), sub: t("sc_collab_indiv"), icon: Users, d: r.ci, accent: C.terra },
  ];
  const best = cols.reduce((a, b) => (b.d.take > a.d.take ? b : a), cols[0]);
  const shortName = { solo: t("chart_solo"), cj: t("chart_cj"), ci: t("chart_ci") };
  const chartData = cols.map((c) => ({ name: shortName[c.key], take: Math.round(c.d.take), key: c.key, accent: c.accent }));
  const takes = chartData.map((d) => d.take);
  const tMin = Math.min(...takes), tMax = Math.max(...takes), tRange = Math.max(1, tMax - tMin);
  const domainMin = Math.max(0, Math.floor((tMin - tRange * 0.9) / 500) * 500);
  const domainMax = Math.ceil((tMax + tRange * 0.5) / 500) * 500;

  return (
    <div style={{ background: C.paper, color: C.ink, fontFamily: "'Hanken Grotesk', sans-serif", minHeight: "100%", width: "100%", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,900&family=Hanken+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');
        html, body, #root { background:${C.paper}; }
        * { box-sizing: border-box; }
        .slider { -webkit-appearance:none; appearance:none; width:100%; height:3px; background:${C.line}; border-radius:2px; outline:none; }
        .slider::-webkit-slider-thumb { -webkit-appearance:none; appearance:none; width:20px; height:20px; border-radius:50%; background:${C.terra}; cursor:pointer; border:3px solid ${C.card}; box-shadow:0 1px 4px rgba(0,0,0,.25); }
        .slider::-moz-range-thumb { width:20px; height:20px; border-radius:50%; background:${C.terra}; cursor:pointer; border:3px solid ${C.card}; }
        .chip { font-size:12.5px; padding:6px 12px; border-radius:999px; border:1.5px solid ${C.line}; cursor:pointer; background:transparent; color:${C.muted}; font-weight:600; transition:all .15s; }
        .chip.on { background:${C.ink}; color:${C.paper}; border-color:${C.ink}; }
        .topbar { position:sticky; top:0; z-index:100; display:flex; align-items:center; justify-content:space-between; gap:1rem; padding:.75rem clamp(1.25rem,4vw,4rem); background:color-mix(in srgb, ${C.paper} 88%, transparent); backdrop-filter:blur(8px); border-bottom:1px solid ${C.line}; }
        .topbar-right { display:flex; align-items:center; gap:clamp(.6rem,1.6vw,1.4rem); }
        .lang-switch { display:inline-flex; align-items:center; gap:.3rem; font-family:'IBM Plex Mono',ui-monospace,monospace; font-size:.82rem; }
        .lang-btn { background:none; border:none; cursor:pointer; font:inherit; color:#70695a; display:inline-flex; align-items:center; justify-content:center; min-width:24px; min-height:24px; padding:.35em .5em; letter-spacing:.05em; }
        .lang-btn[aria-pressed="true"] { color:${C.ink}; font-weight:600; text-decoration:underline; text-decoration-color:#d6451f; text-underline-offset:4px; }
        .lang-btn:hover { color:${C.ink}; }
        .lang-sep { color:#c3b89f; }
        .num { font-family:'DM Mono', monospace; font-variant-numeric: tabular-nums; }
        .main-grid { display:grid; grid-template-columns:minmax(280px,360px) 1fr; gap:32px; align-items:start; }
        .cards-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; }
        .pc-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
        .wrap { max-width:1080px; margin:0 auto; padding:30px 24px 50px; }
        .footer { background:#ece5d4; border-top:1.5px solid ${C.ink}; padding:clamp(2.5rem,5vw,4rem) clamp(1.25rem,4vw,4rem) 2rem; margin-top:44px; }
        .footer-grid { max-width:1080px; margin:0 auto; display:grid; grid-template-columns:1fr auto 1fr; align-items:center; gap:2rem; }
        .footer-brand .brand-word { font-family:'Fraunces',serif; font-weight:700; font-size:1.4rem; }
        .footer-tag { font-family:'IBM Plex Mono',ui-monospace,monospace; font-size:.78rem; color:#4a463d; margin:.4rem 0 0; }
        .footer-stamp { display:flex; flex-direction:column; align-items:center; justify-content:center; border:2px solid #b5462f; color:#b5462f; border-radius:50%; width:9rem; height:9rem; transform:rotate(-8deg); font-family:'IBM Plex Mono',ui-monospace,monospace; text-transform:uppercase; letter-spacing:.08em; line-height:1.5; opacity:.85; }
        .stamp-line--1 { font-size:.82rem; font-weight:600; }
        .stamp-line--2 { font-size:.62rem; }
        .stamp-line--3 { font-size:.7rem; letter-spacing:.2em; }
        .footer-links { display:flex; flex-direction:column; align-items:flex-end; gap:.8rem; }
        .footer-links a { font-family:'IBM Plex Mono',ui-monospace,monospace; font-size:.85rem; color:${C.ink}; text-decoration:none; text-underline-offset:3px; }
        .footer-links a:hover { text-decoration:underline; text-decoration-color:#d6451f; }
        .footer-nav { max-width:1080px; margin:2.2rem auto 0; display:flex; flex-wrap:wrap; gap:1.2rem 1.6rem; font-family:'IBM Plex Mono',ui-monospace,monospace; font-size:.85rem; }
        .footer-nav a { color:${C.ink}; text-decoration:none; }
        .footer-nav a:hover { text-decoration:underline; text-decoration-color:#d6451f; text-underline-offset:3px; }
        .footer-fine { max-width:1080px; margin:2.5rem auto 0; font-family:'IBM Plex Mono',ui-monospace,monospace; font-size:.68rem; color:#70695a; line-height:1.6; border-top:1px solid #c3b89f; padding-top:1.2rem; }
        @media (max-width:760px){ .footer-grid { grid-template-columns:1fr; gap:1.4rem; text-align:center; justify-items:center; } .footer-links { align-items:center; } }
        a.ext { color:${C.terraDeep}; text-decoration:none; border-bottom:1px dotted ${C.terraDeep}; }
        a.ext:hover { color:${C.terra}; }
        a.brand { display:inline-flex; align-items:center; gap:.6rem; text-decoration:none; font-family:'Fraunces',serif; font-weight:700; color:${C.ink}; }
        a.brand:hover { opacity:.78; }
        .brand-mark { display:inline-flex; align-items:flex-end; gap:2px; height:1.2rem; }
        .mark-bar { width:4px; background:${C.ink}; border-radius:1px; }
        .mark-bar:last-child { background:#d6451f; }
        .brand-word { font-size:1.3rem; letter-spacing:-.01em; }
        .brand-score { color:#d6451f; }
        @media (max-width:760px){
          .wrap { padding:18px 14px 40px; }
          .h1 { font-size:26px !important; line-height:1.05 !important; }
          .hdr-sub { text-align:left !important; max-width:none !important; margin-top:8px; }
          .hdr-row { flex-direction:column !important; align-items:flex-start !important; gap:4px !important; }
          .main-grid { grid-template-columns:1fr !important; gap:18px; }
          .cards-grid { grid-template-columns:1fr !important; }
          .pc-grid { grid-template-columns:1fr !important; }
          .verdict-card { flex-direction:column !important; align-items:flex-start !important; gap:10px !important; padding:16px 18px !important; }
          .verdict-title { font-size:18px !important; }
          .controls-card { padding:18px !important; }
        }
        @media (max-width:380px){
          .wrap { padding:14px 10px 36px; }
        }
      `}</style>

      <header className="topbar">
        <a className="brand" href="https://micasascore.com/" aria-label="MiCasaScore home">
          <span className="brand-mark" aria-hidden="true">
            <span className="mark-bar" style={{ height: "38%" }}></span>
            <span className="mark-bar" style={{ height: "64%" }}></span>
            <span className="mark-bar" style={{ height: "92%" }}></span>
          </span>
          <span className="brand-word">MiCasa<span className="brand-score">Score</span></span>
        </a>
        <div className="topbar-right">
          <div className="lang-switch" role="group" aria-label="Language">
            {["en", "es", "uk"].map((l, i) => (
              <React.Fragment key={l}>
                {i > 0 && <span className="lang-sep" aria-hidden="true">/</span>}
                <button type="button" className="lang-btn" aria-pressed={lang === l} onClick={() => setLang(l)}>{LANG_LABELS[l]}</button>
              </React.Fragment>
            ))}
          </div>
        </div>
      </header>

      <div className="wrap">
        {/* Header */}
        <div style={{ borderBottom: `2px solid ${C.ink}`, paddingBottom: 18, marginBottom: 24 }}>
          <div className="hdr-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
            <div style={{ minWidth: 0, flex: "1 1 280px" }}>
              <div style={{ fontSize: 11.5, letterSpacing: ".18em", textTransform: "uppercase", color: C.terra, fontWeight: 700, marginBottom: 6 }}>{t("header_period")}</div>
              <h1 className="h1" style={{ fontFamily: "'Fraunces', serif", fontWeight: 900, fontSize: 38, lineHeight: 1, margin: 0, letterSpacing: "-.02em" }}>
                {t("header_title_pre")} <span style={{ color: C.muted, fontWeight: 400, fontStyle: "italic" }}>{t("header_title_vs")}</span> {t("header_title_post")}
              </h1>
            </div>
            <div className="hdr-sub" style={{ fontSize: 13, color: C.muted, maxWidth: 280, textAlign: "right" }}>{t("header_sub")}</div>
          </div>
        </div>

        <div className="main-grid">
          {/* CONTROLS */}
          <div className="controls-card" style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: 22 }}>
            <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 19, margin: "0 0 18px", fontWeight: 600 }}>{t("ctrl_your_situation")}</h2>

            <Slider label={t("ctrl_revenue")} value={revenue} min={0} max={200000} step={1000} onChange={setRevenue} hint={t("ctrl_revenue_hint")} />
            <Slider label={t("ctrl_expenses")} value={expenses} min={0} max={Math.max(5000, revenue)} step={500} onChange={setExpenses} hint={`${t("ctrl_net_profit")} = ${eur(P)}`} />

            <div style={{ height: 1, background: C.line, margin: "8px 0 18px" }} />

            <Slider label={t("ctrl_split")} value={split} min={10} max={50} step={1} onChange={setSplit} suffix="%" hint={`${t("ctrl_split_hint")}: ${eur(P * split / 100)}`} />

            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 6 }}>
              <div>
                <div style={{ fontSize: 12.5, letterSpacing: ".04em", textTransform: "uppercase", color: C.muted, fontWeight: 600, marginBottom: 8 }}>{t("ctrl_kids")}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[0, 1, 2, 3, 4].map((k) => (
                    <button key={k} className={"chip" + (kids === k ? " on" : "")} onClick={() => setKids(k)} style={{ flex: 1 }}>{k}</button>
                  ))}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12.5, letterSpacing: ".04em", textTransform: "uppercase", color: C.muted, fontWeight: 600, marginBottom: 8 }}>{t("ctrl_year")}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[{ v: 1, l: t("ctrl_year_1") }, { v: 2, l: t("ctrl_year_2") }, { v: 3, l: t("ctrl_year_3plus") }].map((y) => (
                    <button key={y.v} className={"chip" + (years === y.v ? " on" : "")} onClick={() => setYears(y.v)} style={{ flex: 1 }}>{y.l}</button>
                  ))}
                </div>
                {flat && <div style={{ fontSize: 11.5, color: C.terra, marginTop: 5 }}>{t("ctrl_flat_note")}</div>}
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontSize: 12.5, letterSpacing: ".04em", textTransform: "uppercase", color: C.muted, fontWeight: 600 }}>{t("ctrl_spouse")}</div>
                <button className={"chip" + (hasSpouse ? " on" : "")} onClick={() => setHasSpouse(!hasSpouse)}>{hasSpouse ? t("ctrl_yes") : t("ctrl_no")}</button>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", opacity: 0.55, gap: 8 }}>
                <div style={{ fontSize: 12.5, letterSpacing: ".04em", textTransform: "uppercase", color: C.muted, fontWeight: 600 }}>{t("ctrl_region")}</div>
                <span style={{ fontSize: 12.5, fontWeight: 600 }}>{t("ctrl_region_value")}</span>
              </div>
            </div>
          </div>

          {/* RESULTS */}
          <div style={{ minWidth: 0 }}>
            {/* verdict */}
            <div className="verdict-card" style={{ background: C.ink, color: C.paper, borderRadius: 14, padding: "20px 22px", marginBottom: 20, display: "flex", alignItems: "center", gap: 16 }}>
              <TrendingUp size={26} color={C.gold} strokeWidth={2.2} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 11.5, letterSpacing: ".12em", textTransform: "uppercase", color: C.gold, fontWeight: 700, marginBottom: 3 }}>{t("verdict_label")}</div>
                <div className="verdict-title" style={{ fontFamily: "'Fraunces', serif", fontSize: 21, fontWeight: 600, lineHeight: 1.15 }}>
                  {best.name} <span style={{ color: "#cabfa9", fontStyle: "italic", fontWeight: 400, fontSize: 16 }}>· {best.sub}</span>
                </div>
                <div style={{ fontSize: 13, color: "#cabfa9", marginTop: 3 }}>
                  {t("verdict_net")} <span className="num" style={{ color: C.paper, fontWeight: 600 }}>{eur(best.d.take)}</span> {t("verdict_net_suffix")}
                  {best.key !== "solo" && (() => { const diff = best.d.take - r.solo.take; return diff > 0 ? <> — <span style={{ color: "#9fd6c8" }}>{eur(diff)}</span> {t("verdict_more_than_solo")}</> : <> {t("verdict_no_diff")}</>; })()}
                </div>
              </div>
            </div>

            {/* cards */}
            <div className="cards-grid" style={{ marginBottom: 20 }}>
              {cols.map((c) => {
                const isBest = c.key === best.key;
                const Icon = c.icon;
                const effRate = P > 0 ? ((c.d.ss + c.d.tax) / P) * 100 : 0;
                return (
                  <div key={c.key} style={{
                    background: C.card, borderRadius: 12, padding: "17px 15px",
                    border: isBest ? `2px solid ${c.accent}` : `1px solid ${C.line}`,
                    position: "relative", boxShadow: isBest ? "0 6px 20px rgba(0,0,0,.07)" : "none", minWidth: 0,
                  }}>
                    {isBest && <div style={{ position: "absolute", top: -10, right: 12, background: c.accent, color: "#fff", fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", padding: "3px 8px", borderRadius: 6, textTransform: "uppercase" }}>{t("sc_best")}</div>}
                    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12, minWidth: 0 }}>
                      <Icon size={16} color={c.accent} strokeWidth={2.2} style={{ flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.15 }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>{c.sub}</div>
                      </div>
                    </div>
                    <Row label={t("row_ss")} v={c.d.ss} eur={eur} />
                    <Row label={t("row_irpf")} v={c.d.tax} eur={eur} />
                    <div style={{ height: 1, background: C.line, margin: "9px 0" }} />
                    <Row label={t("row_total_tax")} v={c.d.ss + c.d.tax} bold eur={eur} />
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${C.line}` }}>
                      <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 600 }}>{t("row_take_home")}</div>
                      <div className="num" style={{ fontSize: 23, fontWeight: 500, color: c.accent, lineHeight: 1.2 }}>{eur(c.d.take)}</div>
                      <div style={{ fontSize: 11.5, color: C.muted }}>{t("row_pressure")} {effRate.toFixed(1)}%</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* chart */}
            <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: "14px 12px 6px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "4px 4px 10px", flexWrap: "wrap", gap: 4 }}>
                <span style={{ fontSize: 11.5, letterSpacing: ".06em", textTransform: "uppercase", color: C.muted, fontWeight: 700 }}>{t("chart_title")}</span>
                <span style={{ fontSize: 10.5, color: C.muted, fontStyle: "italic" }}>{t("chart_zoom_note")}</span>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top: 26, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid vertical={false} stroke={C.line} strokeDasharray="2 5" />
                  <XAxis dataKey="name" tick={{ fontSize: 12.5, fill: C.ink, fontWeight: 600 }} tickLine={false} axisLine={{ stroke: C.ink }} interval={0} />
                  <YAxis domain={[domainMin, domainMax]} allowDecimals={false}
                    tickFormatter={(v) => (v / 1000).toFixed(0) + "k €"} tick={{ fontSize: 11.5, fill: C.muted }} tickLine={false} axisLine={false} width={44} />
                  <Tooltip formatter={(v) => [eur(v), t("chart_tooltip_net")]} cursor={{ fill: "rgba(0,0,0,.04)" }}
                    contentStyle={{ borderRadius: 8, border: `1px solid ${C.line}`, fontSize: 13 }} />
                  <Bar dataKey="take" radius={[6, 6, 0, 0]} maxBarSize={88}>
                    {chartData.map((d) => <Cell key={d.key} fill={d.accent} fillOpacity={d.key === best.key ? 1 : 0.38} />)}
                    <LabelList dataKey="take" position="top" formatter={(v) => eur(v)}
                      style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 600, fill: C.ink }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* foldable: breakdown */}
            <div style={{ marginTop: 14, background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden" }}>
              <button onClick={() => setShowCalc(!showCalc)} style={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer", color: C.ink, textAlign: "left" }}>
                <span style={{ fontFamily: "'Fraunces', serif", fontSize: 15.5, fontWeight: 600, minWidth: 0 }}>{t("fold_calc_title")}</span>
                <span style={{ fontSize: 18, color: C.terra, transform: showCalc ? "rotate(180deg)" : "none", transition: "transform .2s", flexShrink: 0 }}>▾</span>
              </button>
              {showCalc && (
                <div style={{ padding: "0 16px 18px" }}>
                  <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                    {[{ k: "solo", l: t("fold_view_solo") }, { k: "cj", l: t("fold_view_cj") }, { k: "ci", l: t("fold_view_ci") }].map((tb) => (
                      <button key={tb.k} className={"chip" + (calcView === tb.k ? " on" : "")} onClick={() => setCalcView(tb.k)}>{tb.l}</button>
                    ))}
                  </div>
                  <CalcTable rows={buildRows(calcView, r, kids, t, eur)} eur={eur} />
                </div>
              )}
            </div>

            {/* foldable: pros/cons + inspection */}
            <div style={{ marginTop: 14, background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden" }}>
              <button onClick={() => setShowInfo(!showInfo)} style={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer", color: C.ink, textAlign: "left" }}>
                <span style={{ fontFamily: "'Fraunces', serif", fontSize: 15.5, fontWeight: 600, minWidth: 0 }}>{t("fold_info_title")}</span>
                <span style={{ fontSize: 18, color: C.terra, transform: showInfo ? "rotate(180deg)" : "none", transition: "transform .2s", flexShrink: 0 }}>▾</span>
              </button>
              {showInfo && (
                <div style={{ padding: "0 16px 20px" }}>
                  <div className="pc-grid">
                    <OptionCol title={t("pc_solo")} accent={C.ink} pros={I18N.pros_solo[lang]} cons={I18N.cons_solo[lang]} prosLabel={t("pc_pros")} consLabel={t("pc_cons")} />
                    <OptionCol title={t("pc_collab")} accent={C.terra} pros={I18N.pros_collab[lang]} cons={I18N.cons_collab[lang]} prosLabel={t("pc_pros")} consLabel={t("pc_cons")} />
                  </div>
                  <InfoSection Icon={Gift} color={C.teal} title={t("wife_benefits_title")} items={I18N.wife_benefits[lang]} bulletIcon={Check} />
                  <InfoSection Icon={ShieldCheck} color={C.gold} title={t("req_title")} items={I18N.req_items[lang]} />
                  <InfoSection Icon={AlertTriangle} color={C.terra} title={t("obl_title")} items={I18N.obl_items[lang]} />
                  <InfoSection Icon={AlertTriangle} color={C.terraDeep} title={t("insp_title")} items={I18N.insp_items[lang]} intro={t("insp_intro")} />

                  <div style={{ marginTop: 18 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <ExternalLink size={18} color={C.teal} strokeWidth={2.2} />
                      <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, fontSize: 16 }}>{t("insp_links_title")}</span>
                    </div>
                    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 7 }}>
                      {I18N.insp_links.map((lnk, i) => (
                        <li key={i} style={{ display: "flex", gap: 9, fontSize: 12.5, lineHeight: 1.4 }}>
                          <ExternalLink size={13} color={C.muted} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 3 }} />
                          <a className="ext" href={lnk.url} target="_blank" rel="noopener noreferrer">{lnk.labels[lang]}</a>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div style={{ marginTop: 16, fontSize: 11.5, color: C.muted, lineHeight: 1.5, fontStyle: "italic" }}>{t("note_base_dispute")}</div>
                </div>
              )}
            </div>

            {/* foldable: FAQ */}
            <div style={{ marginTop: 14, background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden" }}>
              <button onClick={() => setShowFaq(!showFaq)} style={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer", color: C.ink, textAlign: "left" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <HelpCircle size={18} color={C.gold} strokeWidth={2.2} />
                  <span style={{ fontFamily: "'Fraunces', serif", fontSize: 15.5, fontWeight: 600 }}>{t("faq_title")}</span>
                </span>
                <span style={{ fontSize: 18, color: C.terra, transform: showFaq ? "rotate(180deg)" : "none", transition: "transform .2s", flexShrink: 0 }}>▾</span>
              </button>
              {showFaq && (
                <div style={{ padding: "0 16px 16px" }}>
                  {FAQ[lang].map((item, i) => {
                    const open = openFaqIdx === i;
                    return (
                      <div key={i} style={{ borderTop: i === 0 ? "none" : `1px solid ${C.line}` }}>
                        <button onClick={() => setOpenFaqIdx(open ? null : i)} style={{
                          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                          padding: "13px 0", background: "transparent", border: "none", cursor: "pointer", color: C.ink, textAlign: "left" }}>
                          <span style={{ fontSize: 14, fontWeight: open ? 700 : 600, color: open ? C.terraDeep : C.ink, lineHeight: 1.35, minWidth: 0 }}>{item.q}</span>
                          <span style={{ fontSize: 16, color: C.terra, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", flexShrink: 0 }}>▾</span>
                        </button>
                        {open && (
                          <div style={{ padding: "0 0 14px", fontSize: 13, lineHeight: 1.55, color: C.ink }}>{item.a}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* notes */}
            <div style={{ display: "flex", gap: 10, marginTop: 18, fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
              <Info size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ minWidth: 0 }}>
                <b>{t("disclaimer_warning")}</b> {t("footnote_body")}
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="footer" id="footer">
        <div className="footer-grid">
          <div className="footer-brand">
            <span className="brand-word">MiCasa<span className="brand-score">Score</span></span>
            <p className="footer-tag">{t("footer_tag")}</p>
          </div>
          <div className="footer-stamp" aria-hidden="true">
            <span className="stamp-line stamp-line--1">{t("footer_stamp1")}</span>
            <span className="stamp-line stamp-line--2">{t("footer_stamp2")}</span>
            <span className="stamp-line stamp-line--3">· VLC ·</span>
          </div>
          <div className="footer-links">
            <a href="https://micasascore.com/privacy.html">{t("footer_privacy")}</a>
            <div className="lang-switch lang-switch--footer" role="group" aria-label="Language">
              {["en", "es", "uk"].map((l, i) => (
                <React.Fragment key={l}>
                  {i > 0 && <span className="lang-sep" aria-hidden="true">/</span>}
                  <button type="button" className="lang-btn" aria-pressed={lang === l} onClick={() => setLang(l)}>{LANG_LABELS[l]}</button>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        <nav className="footer-nav" aria-label="Pages">
          <a href="https://micasascore.com/">{t("footer_nav_home")}</a>
          <a href="https://micasascore.com/info/calculadora-autonomo/">{t("footer_nav_calc")}</a>
          <a href="https://micasascore.com/info/who-pays-estate-agent-spain/">{t("footer_nav_guide")}</a>
        </nav>
        <p className="footer-fine">{t("footer_fine")}</p>
      </footer>
    </div>
  );
}

function Row({ label, v, bold, eur }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "2px 0", gap: 8 }}>
      <span style={{ fontSize: 12.5, color: bold ? C.ink : C.muted, fontWeight: bold ? 700 : 500, minWidth: 0 }}>{label}</span>
      <span className="num" style={{ fontSize: bold ? 15 : 13.5, fontWeight: bold ? 600 : 400, flexShrink: 0 }}>{eur(v)}</span>
    </div>
  );
}
